import { ROUTES } from '../../../constants/routes';
import { ACCESS, AUTHZ_FEATURES } from '../../../constants/authz';
import { globalpermissions } from '../../../Services/Permission';

/**
 * @typedef {Object} QuickLinkDefinition
 * @property {string} id
 * @property {string} key
 * @property {string} iconKey
 * @property {string} title
 * @property {string} description
 * @property {string} route
 * @property {import('../../../authorization/types/authz.types').AuthzRequirement} [access]
 */

/** @type {QuickLinkDefinition[]} */
export const QUICK_LINKS_CATALOG = [
  {
    id: 'intervene',
    key: 'intervene',
    iconKey: 'intervene',
    title: 'Intervene',
    description: 'Open the Intervene workspace for live customer conversations.',
    route: ROUTES.FEATURES.INTERVENE,
    access: ACCESS.INTERVENE_VIEW,
  },
  {
    id: 'profile-management',
    key: 'profile-management',
    iconKey: 'accountCircle',
    title: 'Profile Management',
    description: 'Manage permission profiles for your organisation.',
    route: ROUTES.MANAGEMENT.PROFILE_MANAGEMENT,
    access: ACCESS.PROFILE_MANAGEMENT_VIEW,
  },
  {
    id: 'business-functions',
    key: 'business-functions',
    iconKey: 'campaign',
    title: 'Business Functions',
    description: 'Manage business functions and services for your organisation.',
    route: ROUTES.CAMPAIGN.BUSINESS_FUNCTIONS,
    access: ACCESS.BUSINESS_FUNCTIONS_ACCESS,
  },
  {
    id: 'datasets',
    key: 'datasets',
    iconKey: 'map',
    title: 'Datasets',
    description: 'Manage self-service forms and dataset records.',
    route: ROUTES.FEATURES.DATASETS,
    access: ACCESS.DATASETS_VIEW,
  },
  {
    id: 'flow-builder',
    key: 'flow-builder',
    iconKey: 'accountTree',
    title: 'Flow Builder',
    description: 'Create and publish automation flows.',
    route: ROUTES.FEATURES.FLOW_LIST,
    access: ACCESS.FLOW_VIEW,
  },
  {
    id: 'records',
    key: 'records',
    iconKey: 'howToReg',
    title: 'Records',
    description: 'View campaign leads and records.',
    route: ROUTES.LEGACY.RECORDS,
    access: ACCESS.SUPER_ADMIN_ONLY,
  },
  {
    id: 'whatsapp-templates',
    key: 'whatsapp-templates',
    iconKey: 'whatsapp',
    title: 'WhatsApp Templates',
    description: 'Manage WhatsApp message templates.',
    route: ROUTES.FEATURES.WHATSAPP_TEMPLATES,
    access: ACCESS.WHATSAPP_TEMPLATES_VIEW,
  },
  {
    id: 'analytics',
    key: 'analytics',
    iconKey: 'analytics',
    title: 'Analytics',
    description: 'View entity and performance reports and analytics.',
    route: ROUTES.FEATURES.ANALYTICS,
    access: ACCESS.REPORTING_VIEW,
  },
  // {
  //   id: 'reporting',
  //   key: 'reporting',
  //   iconKey: 'assessment',
  //   title: 'Reporting',
  //   description: 'View entity and performance reports.',
  //   route: ROUTES.LEGACY.REPORTING,
  //   access: ACCESS.REPORTING_VIEW,
  // },
  {
    id: 'ops-dashboard',
    key: 'ops-dashboard',
    iconKey: 'dashboard',
    title: 'Operations Dashboard',
    description: 'Monitor operational dashboards.',
    route: ROUTES.DASHBOARD.OPS,
    access: ACCESS.SUPER_ADMIN_ONLY,
  },
  {
    id: 'concept-builder',
    key: 'concept-builder',
    iconKey: 'school',
    title: 'Concept Builder',
    description: 'Build and manage learning concepts.',
    route: ROUTES.LEGACY.STUDENT_CONCEPT_BUILDER,
    access: ACCESS.EDTECH_CONCEPT_BUILDER,
    legacyPermission: AUTHZ_FEATURES.EDTECH.CONCEPT_BUILDER,
  },
  {
    id: 'practice-test',
    key: 'practice-test',
    iconKey: 'quiz',
    title: 'Practice Test',
    description: 'Create and manage practice tests.',
    route: ROUTES.LEGACY.STUDENT_CREATE_TEST,
    access: ACCESS.EDTECH_PRACTICE_TEST,
    legacyPermission: AUTHZ_FEATURES.EDTECH.PRACTICE_TEST,
  },
  {
    id: 'knowledge-map',
    key: 'knowledge-map',
    iconKey: 'autoStories',
    title: 'Knowledge Map',
    description: 'Explore and manage knowledge maps.',
    route: ROUTES.LEGACY.STUDENT_KNOWLEDGE_MAP,
    access: ACCESS.EDTECH_KNOWLEDGE_MAP,
    legacyPermission: AUTHZ_FEATURES.EDTECH.KNOWLEDGE_MAP,
  },
  {
    id: 'performance-map',
    key: 'performance-map',
    iconKey: 'timeline',
    title: 'Performance Map',
    description: 'Review learner performance and insights.',
    route: ROUTES.LEGACY.STUDENT_PERFORMANCE,
    access: ACCESS.EDTECH_PERFORMANCE_MAP,
    legacyPermission: AUTHZ_FEATURES.EDTECH.PERFORMANCE_MAP,
  },
  {
    id: 'parent-dashboard',
    key: 'parent-dashboard',
    iconKey: 'familyRestroom',
    title: 'Parent Dashboard',
    description: 'View learner progress for parents.',
    route: ROUTES.DASHBOARD.PARENT,
    access: ACCESS.EDTECH_PARENT_DASHBOARD,
    legacyPermission: AUTHZ_FEATURES.EDTECH.PARENT_DASHBOARD,
  },
  {
    id: 'teacher-dashboard',
    key: 'teacher-dashboard',
    iconKey: 'personPin',
    title: 'Teacher Dashboard',
    description: 'View classroom and learner progress.',
    route: ROUTES.DASHBOARD.TEACHER,
    access: ACCESS.EDTECH_TEACHER_DASHBOARD,
    legacyPermission: AUTHZ_FEATURES.EDTECH.TEACHER_DASHBOARD,
  },
  {
    id: 'academic-info',
    key: 'academic-info',
    iconKey: 'infoOutlined',
    title: 'Academic Info',
    description: 'View academic notices and information.',
    route: ROUTES.LEGACY.STUDENT_NOTICES,
    access: ACCESS.EDTECH_ACADEMIC_INFO,
    legacyPermission: AUTHZ_FEATURES.EDTECH.ACADEMIC_INFO,
  },
  {
    id: 'question-bank',
    key: 'question-bank',
    iconKey: 'localLibrary',
    title: 'Question Bank',
    description: 'Browse and manage questions.',
    route: ROUTES.LEGACY.STUDENT_QUESTION_BANK,
    access: ACCESS.EDTECH_QUESTION_BANK,
    legacyPermission: AUTHZ_FEATURES.EDTECH.QUESTION_BANK,
  },
  {
    id: 'syllabus-builder',
    key: 'syllabus-builder',
    iconKey: 'menuBook',
    title: 'Syllabus Builder',
    description: 'Build and manage academic syllabuses.',
    route: ROUTES.LEGACY.STUDENT_PLATFORM,
    access: ACCESS.EDTECH_SYLLABUS_BUILDER,
    legacyPermission: AUTHZ_FEATURES.EDTECH.SYLLABUS_BUILDER,
  },
  {
    id: 'ai',
    key: 'ai',
    iconKey: 'smartToy',
    title: 'AI',
    description: 'Open AI tools and dashboards.',
    route: ROUTES.LEGACY.AI,
    access: ACCESS.AI_DASHBOARD_VIEW,
    legacyPermission: AUTHZ_FEATURES.EDTECH.AI,
  },
  {
    id: 'vector-database',
    key: 'vector-database',
    iconKey: 'hub',
    title: 'Vector Database',
    description: 'Manage vector knowledge and data.',
    route: ROUTES.LEGACY.VECTOR,
    access: ACCESS.VECTOR_DATABASE_VIEW,
    legacyPermission: AUTHZ_FEATURES.EDTECH.VECTOR_DATABASE,
  },
  // {
  //   id: 'tools',
  //   key: 'tools',
  //   iconKey: 'dataObject',
  //   title: 'Tools',
  //   description: 'Open data and content utility tools.',
  //   route: ROUTES.LEGACY.TOOLS,
  //   access: ACCESS.TOOLS_VIEW,
  //   legacyPermission: globalpermissions?.pagePermissions?.urlGeneratorPageAccess,
  // },
];

/** @returns {QuickLinkDefinition[]} */
export function buildQuickLinksCatalog() {
  return QUICK_LINKS_CATALOG;
}
